import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Globe, Mail, Phone } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-md' 
          : 'bg-sky-100 backdrop-blur-md border-b border-sky-200 shadow-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-16 w-auto"
              />
            </Link>
            <Link to="/" className="flex items-center gap-2 text-navy hover:text-gold transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-r from-navy-900 to-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 text-gold mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-slate-300">
            Your privacy is important to us
          </p>
          <p className="text-sm text-slate-700 mt-4">
            Last Updated: December 2025
          </p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Quick Overview */}
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm text-center">
              <Lock className="w-8 h-8 text-gold mx-auto mb-3" />
              <h3 className="font-bold text-navy text-sm">Data Encryption</h3>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm text-center">
              <Eye className="w-8 h-8 text-gold mx-auto mb-3" />
              <h3 className="font-bold text-navy text-sm">Transparency</h3>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm text-center">
              <Database className="w-8 h-8 text-gold mx-auto mb-3" />
              <h3 className="font-bold text-navy text-sm">Secure Storage</h3>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm text-center">
              <UserCheck className="w-8 h-8 text-gold mx-auto mb-3" />
              <h3 className="font-bold text-navy text-sm">User Control</h3>
            </div>
          </div>

          {/* Detailed Policy */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
            
            {/* Section 1 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">1</span>
                Introduction
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                My Corporate Hub ("we", "us", "our"), operated by Big Kreation Entertainment Pvt Ltd, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
              </p>
              <p className="text-slate-700 leading-relaxed">
                By using our platform, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this privacy policy, please do not access the site.
              </p>
            </div>

            {/* Section 2 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">2</span>
                Information We Collect
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-navy mb-3">2.1 Personal Information</h3>
                  <p className="text-slate-700 mb-3">We may collect personally identifiable information, including but not limited to:</p>
                  <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                    <li>Name and contact information (email, phone number)</li>
                    <li>Company/Organization name and designation</li>
                    <li>Billing and payment information</li>
                    <li>Event details and preferences</li>
                    <li>Communication history with our platform</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-navy mb-3">2.2 Automatically Collected Information</h3>
                  <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                    <li>IP address and browser type</li>
                    <li>Device information and operating system</li>
                    <li>Pages visited and time spent on pages</li>
                    <li>Referring website addresses</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">3</span>
                How We Use Your Information
              </h2>
              <p className="text-slate-700 mb-4">We use the collected information for various purposes:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>To provide and maintain our services</li>
                <li>To process bookings and transactions</li>
                <li>To communicate with you about your bookings and inquiries</li>
                <li>To send promotional communications (with your consent)</li>
                <li>To improve our website and services</li>
                <li>To detect, prevent, and address technical issues</li>
                <li>To comply with legal obligations</li>
                <li>To facilitate vendor-client communications</li>
              </ul>
            </div>

            {/* Section 4 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">4</span>
                Information Sharing and Disclosure
              </h2>
              <p className="text-slate-700 mb-4">We may share your information in the following situations:</p>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                  <div><strong>With Vendors:</strong> To facilitate bookings and service delivery</div>
                </li>
                <li className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                  <div><strong>Payment Processors:</strong> To process transactions securely via Razorpay</div>
                </li>
                <li className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                  <div><strong>Legal Requirements:</strong> When required by law or to protect our rights</div>
                </li>
                <li className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                  <div><strong>Business Transfers:</strong> In connection with mergers or acquisitions</div>
                </li>
              </ul>
              <p className="text-slate-700 mt-4 bg-blue-50 p-4 rounded-lg">
                <strong>Note:</strong> We do not sell, trade, or rent your personal information to third parties for marketing purposes.
              </p>
            </div>

            {/* Section 5 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">5</span>
                Data Security
              </h2>
              <p className="text-slate-700 mb-4">
                We implement appropriate security measures to protect your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>SSL/TLS encryption for data transmission</li>
                <li>Secure payment processing through Razorpay (PCI DSS compliant)</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Encrypted database storage</li>
              </ul>
            </div>

            {/* Section 6 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">6</span>
                Cookies Policy
              </h2>
              <p className="text-slate-700 mb-4">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (with consent)</li>
              </ul>
              <p className="text-slate-700 mt-4">
                You can control cookie settings through your browser preferences.
              </p>
            </div>

            {/* Section 7 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">7</span>
                Your Rights
              </h2>
              <p className="text-slate-700 mb-4">You have the following rights regarding your personal data:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Request transfer of your data</li>
              </ul>
            </div>

            {/* Section 8 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">8</span>
                Data Retention
              </h2>
              <p className="text-slate-700">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. Booking records are typically retained for 7 years for tax and legal compliance purposes.
              </p>
            </div>

            {/* Section 9 - Data Protection for Uploads */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">9</span>
                Data Protection for Uploads
              </h2>
              <p className="text-slate-700 mb-4">
                When you upload files to our platform (such as company logos, resumes, profile pictures, or documents), we implement specific protections to safeguard your data:
              </p>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-navy mb-2">Types of Uploads We Accept</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-700 ml-2">
                    <li><strong>Profile Documents:</strong> Resumes, CVs, and cover letters for job applications</li>
                    <li><strong>Company Assets:</strong> Logos and branding materials for gift customization</li>
                    <li><strong>Business Documents:</strong> RFP attachments and event specifications</li>
                    <li><strong>Identity Verification:</strong> Documents for vendor/partner verification (if required)</li>
                  </ul>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-navy mb-2">How We Protect Your Uploads</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-700 ml-2">
                    <li><strong>Encryption:</strong> All uploaded files are encrypted during transmission (SSL/TLS) and at rest</li>
                    <li><strong>Access Control:</strong> Only authorized personnel and intended recipients can access your files</li>
                    <li><strong>Secure Storage:</strong> Files are stored on secure, access-controlled servers</li>
                    <li><strong>Virus Scanning:</strong> All uploads are scanned for malware before processing</li>
                    <li><strong>File Type Validation:</strong> We only accept safe file formats (PDF, DOC, DOCX, JPG, PNG, etc.)</li>
                  </ul>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-navy mb-2">Upload Retention & Deletion</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-700 ml-2">
                    <li><strong>Job Applications:</strong> Resumes retained for 12 months, then automatically deleted unless hired</li>
                    <li><strong>Company Logos:</strong> Retained while your account is active; deleted upon account closure</li>
                    <li><strong>Event Documents:</strong> Retained for 2 years post-event for reference and disputes</li>
                    <li><strong>Deletion Requests:</strong> You can request deletion of your uploaded files at any time by contacting us</li>
                  </ul>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-navy mb-2">Your Rights Over Uploaded Data</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-700 ml-2">
                    <li><strong>Access:</strong> Request copies of all files you have uploaded</li>
                    <li><strong>Correction:</strong> Replace or update your uploaded documents at any time</li>
                    <li><strong>Deletion:</strong> Request permanent deletion of specific or all uploaded files</li>
                    <li><strong>Portability:</strong> Export your uploaded data in common formats</li>
                  </ul>
                </div>
              </div>

              <p className="text-slate-700 mt-4 text-sm bg-slate-100 p-3 rounded-lg">
                <strong>Note:</strong> We never share your uploaded documents with third parties without your explicit consent, except as required by law or to fulfill services you have requested (e.g., sharing your RFP with vendors you select).
              </p>
            </div>

            {/* Section 10 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">10</span>
                Third-Party Links
              </h2>
              <p className="text-slate-700">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read the privacy policies of any linked websites you visit.
              </p>
            </div>

            {/* Section 11 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">11</span>
                Changes to This Policy
              </h2>
              <p className="text-slate-700">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </div>

            {/* Contact Section */}
            <div className="p-8 bg-slate-50">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">12</span>
                Contact Us
              </h2>
              <p className="text-slate-700 mb-6">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <a href="mailto:privacy@mycorporatehub.com" className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200 hover:border-gold transition-colors">
                  <Mail className="w-6 h-6 text-gold" />
                  <div>
                    <p className="text-sm text-slate-700">Email</p>
                    <p className="font-medium text-navy">privacy@mycorporatehub.com</p>
                  </div>
                </a>
                <a href="https://wa.me/919664870766" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200 hover:border-gold transition-colors">
                  <Phone className="w-6 h-6 text-gold" />
                  <div>
                    <p className="text-sm text-slate-700">WhatsApp</p>
                    <p className="font-medium text-navy">+91 96648 70766</p>
                  </div>
                </a>
              </div>
              <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
                <p className="text-sm text-slate-800">
                  <strong>Registered Office:</strong><br />
                  Big Kreation Entertainment Pvt Ltd<br />
                  L23, Express Zone, A Wing, Western Express Highway,<br />
                  Panch Bawadi, Malad East, Mumbai 400063,<br />
                  Maharashtra, India
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-700 text-sm">
            © 2025 My Corporate Hub. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms-conditions" className="text-slate-700 hover:text-white text-sm transition-colors">Terms & Conditions</Link>
            <Link to="/refund-policy" className="text-slate-700 hover:text-white text-sm transition-colors">Refund Policy</Link>
            <Link to="/" className="text-slate-700 hover:text-white text-sm transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
